/**
 * Bot selection / practice lobby screen.
 * Allows the player to choose a bot opponent, their team, and battle settings.
 */

import type { ScreenId } from "./home";
import type { BattleConfig, Team } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, dropdown, numberInput, toast } from "../components";
import { BUILT_IN_BOTS } from "../../engine/bots";
import { BOT_PRESETS } from "../../data/bots";
import { loadTrainerProfile } from "./trainer";
import { getAllTeams } from "../../data/teams";
import { BATTLE_BACKGROUNDS } from "../../data/assets";
import { createBotBattleSession } from "../bot-session";
import { generateSeed } from "../../engine/battle";
import { DEFAULT_LEVEL, DEFAULT_TURN_TIMER } from "../../config/constants";

type BotOption = { id: string; label: string };

function buildBotOptions(): BotOption[] {
  const options: BotOption[] = [
    ...BUILT_IN_BOTS.map((b) => ({ id: `builtin:${b.id}`, label: b.name })),
    ...BOT_PRESETS.map((p) => ({ id: `preset:${p.id}`, label: p.name })),
  ];
  return options;
}

function resolveBotById(id: string): { bot: import("../../engine/types").IBotPlayer; background?: string } | null {
  if (id.startsWith("builtin:")) {
    const botId = id.slice("builtin:".length);
    const bot = BUILT_IN_BOTS.find((b) => b.id === botId);
    return bot ? { bot } : null;
  }
  if (id.startsWith("preset:")) {
    const presetId = id.slice("preset:".length);
    const preset = BOT_PRESETS.find((p) => p.id === presetId);
    return preset ? { bot: preset.bot, background: preset.background } : null;
  }
  return null;
}

export function renderBotLobby(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: "Practice Battle" }));
  header.appendChild(button("Back to Home", () => navigate("home"), { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main bot-lobby-main" });
  container.appendChild(main);

  // State
  let selectedBotId = "builtin:first-legal";
  let selectedTeamId: string | null = null;
  let playerTeam: Team | null = null;
  let battleLevel = DEFAULT_LEVEL;
  let battleTimer = DEFAULT_TURN_TIMER;
  let battleBg = "grass";
  let teamsList: Team[] = [];

  // Bot selection
  const botOptions = buildBotOptions();
  const botDropdown = dropdown("Opponent", botOptions.map((o) => ({ value: o.id, label: o.label })), selectedBotId, (val) => {
    selectedBotId = val;
    const resolved = resolveBotById(val);
    if (resolved?.background) {
      battleBg = resolved.background;
    }
    updatePreview();
  });
  main.appendChild(botDropdown.container);

  // Bot description / preview
  const previewArea = el("div", { className: "bot-preview" });
  main.appendChild(previewArea);

  function updatePreview(): void {
    previewArea.innerHTML = "";
    const resolved = resolveBotById(selectedBotId);
    if (!resolved) return;

    // Show preset description if available
    if (selectedBotId.startsWith("preset:")) {
      const presetId = selectedBotId.slice("preset:".length);
      const preset = BOT_PRESETS.find((p) => p.id === presetId);
      if (preset) {
        previewArea.appendChild(el("p", { className: "bot-description", text: preset.description }));
      }
    } else {
      const botId = selectedBotId.slice("builtin:".length);
      const bot = BUILT_IN_BOTS.find((b) => b.id === botId);
      if (bot) {
        previewArea.appendChild(el("p", { className: "bot-description", text: `Plays with a ${bot.name} strategy. Team will be randomized.` }));
      }
    }
  }
  updatePreview();

  // Team selector
  void loadTeams();
  async function loadTeams(): Promise<void> {
    teamsList = await getAllTeams();
    const teamOptions = teamsList.map((t) => ({ value: t.id, label: t.name }));
    teamOptions.unshift({ value: "", label: "Random Team" });

    const teamDropdown = dropdown("Your Team", teamOptions, selectedTeamId ?? "", (val) => {
      selectedTeamId = val || null;
      if (val) {
        playerTeam = teamsList.find((t) => t.id === val) ?? null;
      } else {
        playerTeam = null;
      }
    });
    // Insert after preview
    main.insertBefore(teamDropdown.container, main.children[main.children.length - 1]);
  }

  // Battle config
  const configSection = el("div", { className: "bot-config-section" });

  const levelInput = numberInput("Level", battleLevel, (val) => { battleLevel = val; }, { min: 1, max: 100, step: 5 });
  configSection.appendChild(levelInput.container);

  const timerInput = numberInput("Turn Timer (seconds, 0 = off)", battleTimer, (val) => { battleTimer = val; }, { min: 0, max: 300, step: 10 });
  configSection.appendChild(timerInput.container);

  const bgOptions = BATTLE_BACKGROUNDS.map((b) => ({ value: b.id, label: b.name }));
  const bgDropdown = dropdown("Background", bgOptions, battleBg, (val) => { battleBg = val; });
  configSection.appendChild(bgDropdown.container);

  main.appendChild(configSection);

  // Start button
  const startBtn = button("Start Practice Battle", async () => {
    startBtn.disabled = true;

    const resolved = resolveBotById(selectedBotId);
    if (!resolved) {
      toast("Invalid bot selection", "error");
      startBtn.disabled = false;
      return;
    }

    // Resolve player team
    let team: Team;
    if (playerTeam) {
      team = playerTeam;
    } else {
      const { randomizeTeam } = await import("../../engine/randomizer");
      team = await randomizeTeam();
    }

    const playerProfile = loadTrainerProfile();

    const config: BattleConfig = {
      level: battleLevel,
      turnTimerSeconds: battleTimer,
      battleBackground: battleBg,
    };

    const seed = generateSeed();

    let botTeam: Team | undefined;
    if (selectedBotId.startsWith("preset:")) {
      const presetId = selectedBotId.slice("preset:".length);
      const preset = BOT_PRESETS.find((p) => p.id === presetId);
      if (preset) {
        botTeam = { id: preset.id, name: preset.name, format: "gen5", members: preset.team, createdAt: "", updatedAt: "" };
      }
    }

    try {
      await createBotBattleSession({
        playerTeam: team,
        bot: resolved.bot,
        botTeam,
        battleConfig: config,
        seed,
      }, playerProfile);

      navigate("bot-battle");
    } catch (err) {
      toast(`Failed to start battle: ${err instanceof Error ? err.message : String(err)}`, "error");
      startBtn.disabled = false;
    }
  });
  main.appendChild(startBtn);

  // Custom team selector: "Load from saved teams" prompt
  if (teamsList.length === 0) {
    void (async () => {
      teamsList = await getAllTeams();
      if (teamsList.length > 0) {
        toast("Saved teams loaded — select one from the dropdown", "info");
      }
    })();
  }
}